import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "@/lib/router-compat";
import type { WebinarSpeaker } from "@/lib/parseWebinarContent";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";

export interface AuthorMatch {
  name: string;
  slug: string;
  avatar_url: string | null;
  role: string | null;
  bio: string | null;
  bio_es?: string | null;
}

interface WebinarSpeakersProps {
  speakers: WebinarSpeaker[];
  intro?: string;
  authors?: AuthorMatch[];
}

/**
 * Try to find a matching author for a speaker by comparing names.
 * Uses a loose match: checks if the author name appears within the speaker name or vice versa.
 */
function findAuthorMatch(speakerName: string, authors: AuthorMatch[]): AuthorMatch | undefined {
  const normalise = (s: string) =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z\s]/g, "").trim();
  const speakerNorm = normalise(speakerName);

  // Exact match first
  const exact = authors.find((a) => normalise(a.name) === speakerNorm);
  if (exact) return exact;

  // Partial: author name contained in speaker name (handles "Dr. Name, Title")
  return authors.find((a) => {
    const aN = normalise(a.name);
    return speakerNorm.includes(aN) || aN.includes(speakerNorm);
  });
}

const WebinarSpeakers = ({ speakers, intro, authors = [] }: WebinarSpeakersProps) => {
  const lang = useLanguage();
  const t = uiT(lang);

  if (speakers.length === 0) return null;

  const threeUp = speakers.length >= 3;

  return (

    <div className="mt-16">
      <div className="mb-10">
        <p className="text-xs font-bold uppercase tracking-wider text-accent mb-2">
          {t("learnFromBest")}
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground">{t("speakers")}</h2>
        {intro && (
          <p className="mt-3 text-muted-foreground leading-relaxed max-w-2xl">{intro}</p>
        )}
      </div>

      <div
        className={`grid grid-cols-1 items-stretch auto-rows-fr gap-6 ${
          speakers.length >= 3
            ? "md:grid-cols-2 lg:grid-cols-3"
            : speakers.length === 2
              ? "md:grid-cols-2"
              : ""
        }`}
      >
        {speakers.map((speaker, i) => {

          const author = findAuthorMatch(speaker.name, authors);
          const avatarUrl = author?.avatar_url || speaker.imageUrl;
          const displayRole = speaker.role || author?.role || "";
          const isEs = lang === "es";
          const rawBio = (isEs && author?.bio_es) ? author.bio_es : (speaker.bio || author?.bio || "");
          const displayBio = rawBio.replace(/^\[.*\]$/, "").replace(/\*\*/g, "").replace(/(?<!\w)\*(?!\w)/g, "").trim();

          return (
            <SpeakerCard
              key={speaker.name}
              speaker={speaker}
              author={author}
              avatarUrl={avatarUrl}
              displayRole={displayRole}
              displayBio={displayBio}
              index={i}
              stacked={threeUp}
            />

          );
        })}
      </div>
    </div>
  );
};

interface SpeakerCardProps {
  speaker: WebinarSpeaker;
  author?: AuthorMatch;
  avatarUrl: string | null;
  displayRole: string;
  displayBio: string;
  index: number;
  stacked?: boolean;
}


const SpeakerCard = ({ speaker, author, avatarUrl, displayRole, displayBio, index, stacked = false }: SpeakerCardProps) => {
  const lang = useLanguage();
  const pre = langPrefix(lang);
  const [imgError, setImgError] = useState(false);
  const showImage = avatarUrl && !imgError;

  const initials = speaker.name
    .split(/\s+/)
    .map((w) => w.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const card = (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      className="bg-secondary rounded-2xl p-6 border border-border hover:shadow-lg transition-shadow h-full min-h-[17.5rem] flex flex-col"
    >
      <div
        className={
          stacked
            ? "flex flex-col items-start gap-3 text-left"
            : "flex flex-col lg:flex-row items-center lg:items-start gap-4 text-center lg:text-left"
        }
      >

        {showImage ? (
          <img
            src={avatarUrl}
            alt={speaker.name}
            className="w-16 h-16 lg:w-20 lg:h-20 rounded-full object-cover shrink-0 ring-2 ring-accent/20"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-accent/10 flex items-center justify-center text-accent text-xl font-bold shrink-0 ring-2 ring-accent/20">
            {initials}
          </div>
        )}
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-bold text-foreground">{speaker.name}</h3>
          <p className="text-xs sm:text-sm text-accent font-medium mt-0.5 leading-snug break-words">{displayRole}</p>
        </div>
      </div>
      <p className="mt-4 text-sm text-muted-foreground leading-relaxed flex-1 break-words">
        {displayBio || "\u00A0"}
      </p>
    </motion.div>
  );

  if (author) {
    return (
      <Link to={`${pre}/author/${author.slug}`} className="block h-full">
        {card}
      </Link>
    );
  }

  return card;
};

export default WebinarSpeakers;
